import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { repositorioActuadores } from '../../../data/repositories/RepositorioActuadores';
import { repositorioTipos } from '../../../data/repositories/RepositorioTipos';
import type { CampoConfiguracion } from '../../../domain/value-objects/EsquemaConfiguracion';
import { ErrorApp, registrarError } from '../../../domain/errores';
import estilos from './ConfigurarActuador.module.css';

interface ConfigurarActuadorProps {
  abierto: boolean;
  actuadorId: number | null;
  actuadorNombre: string;
  tipoId: number | null;
  clienteId: number | null;
  /** Ajustes ya guardados en el actuador, por encima de los de fábrica. */
  configActual: Record<string, unknown>;
  onCerrar: () => void;
  onGuardado: () => void;
}

/** Texto con el que se muestra un valor de fábrica junto al campo. */
function comoTextoBase(campo: CampoConfiguracion, valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (campo.tipo === 'bool') return valor === true || valor === 'true' ? 'Sí' : 'No';
  if (Array.isArray(valor)) return valor.length ? valor.join(', ') : '—';
  return String(valor);
}

/**
 * Ajuste fino de un actuador según su tipo.
 *
 * El formulario no está escrito aquí: se dibuja a partir del esquema que el
 * tipo declara en la base. Un tipo nuevo con campos distintos funciona sin
 * tocar la consola, que es el sentido de tener el esquema en datos.
 */
export function ConfigurarActuador({
  abierto,
  actuadorId,
  actuadorNombre,
  tipoId,
  clienteId,
  configActual,
  onCerrar,
  onGuardado,
}: ConfigurarActuadorProps) {
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const esquema = useQuery({
    queryKey: ['tipos', 'actuadores', 'esquema', clienteId, tipoId],
    queryFn: () => repositorioTipos.esquemaDeTipoActuador(clienteId ?? 0, tipoId ?? 0),
    enabled: abierto && clienteId !== null && tipoId !== null,
    staleTime: 5 * 60_000,
  });

  // Lo que se edita es el valor efectivo: fábrica más lo ya personalizado.
  useEffect(() => {
    if (!abierto || !esquema.data) return;

    setError(null);
    setValores({ ...esquema.data.valoresBase, ...configActual });
  }, [abierto, esquema.data, configActual]);

  const guardar = useMutation({
    mutationFn: (config: Record<string, unknown>) =>
      repositorioActuadores.configurar(actuadorId ?? 0, config),
    onSuccess: () => {
      toast.success('Configuración guardada');
      onGuardado();
    },
    onError: (e) => {
      registrarError('actuadores.configurar', e);
      setError(e instanceof ErrorApp ? e.message : 'No se pudo guardar la configuración');
    },
  });

  const campos = useMemo(() => esquema.data?.campos ?? [], [esquema.data]);

  const cambiar = (clave: string, valor: unknown) => {
    setValores((previos) => ({ ...previos, [clave]: valor }));
  };

  const enviar = () => {
    setError(null);

    // Los números se validan aquí para poder señalar el campo con palabras;
    // el servidor revalida todo contra el esquema de todas formas.
    for (const campo of campos) {
      if (campo.tipo !== 'int' && campo.tipo !== 'number') continue;

      const crudo = valores[campo.clave];
      if (crudo === null || crudo === undefined || crudo === '') continue;

      const numero = Number(crudo);
      if (!Number.isFinite(numero)) {
        setError(`«${campo.etiqueta}» debe ser un número.`);
        return;
      }
      if (campo.min !== null && numero < campo.min) {
        setError(`«${campo.etiqueta}» no puede ser menor que ${campo.min}.`);
        return;
      }
      if (campo.max !== null && numero > campo.max) {
        setError(`«${campo.etiqueta}» no puede ser mayor que ${campo.max}.`);
        return;
      }
    }

    const config: Record<string, unknown> = {};
    for (const campo of campos) {
      const valor = valores[campo.clave];

      if (campo.tipo === 'int' || campo.tipo === 'number') {
        config[campo.clave] =
          valor === null || valor === undefined || valor === '' ? null : Number(valor);
      } else {
        config[campo.clave] = valor ?? null;
      }
    }

    guardar.mutate(config);
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={`Configurar ${actuadorNombre}`}
      descripcion="Ajustes propios de este equipo. Lo que no cambies queda con el valor de fábrica de su tipo."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            icono="guardar"
            cargando={guardar.isPending}
            motivoDeshabilitado={
              campos.length === 0 ? 'Este tipo no tiene nada configurable' : null
            }
            onClick={enviar}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && (
          <div className={estilos.error}>
            <Icon nombre="errorContorno" tamano={15} />
            {error}
          </div>
        )}

        {esquema.isLoading ? (
          <p className={estilos.secundario}>Cargando el esquema del tipo…</p>
        ) : esquema.isError ? (
          <p className={estilos.secundario}>
            No se pudo leer la configuración del tipo. Cierra y vuelve a intentarlo.
          </p>
        ) : campos.length === 0 ? (
          <p className={estilos.secundario}>
            El tipo «{esquema.data?.tipoNombre}» no declara ajustes configurables.
          </p>
        ) : (
          campos.map((campo) => {
            const valor = valores[campo.clave];
            const base = comoTextoBase(campo, esquema.data?.valoresBase[campo.clave]);
            const pieBase = `De fábrica: ${base}${campo.unidad && base !== '—' ? ` ${campo.unidad}` : ''}`;
            const ayuda = campo.ayuda ? `${campo.ayuda} · ${pieBase}` : pieBase;

            if (campo.tipo === 'bool') {
              return (
                <label key={campo.clave} className={estilos.opcion}>
                  <input
                    type="checkbox"
                    checked={valor === true || valor === 'true'}
                    onChange={(e) => cambiar(campo.clave, e.target.checked)}
                  />
                  <span>
                    {campo.etiqueta}
                    {campo.requerido && ' *'}
                    <span className={estilos.opcionAyuda}>{ayuda}</span>
                  </span>
                </label>
              );
            }

            if (campo.tipo === 'enum') {
              return (
                <Select
                  key={campo.clave}
                  etiqueta={campo.etiqueta}
                  requerido={campo.requerido}
                  value={valor === null || valor === undefined ? '' : String(valor)}
                  onChange={(e) => cambiar(campo.clave, e.target.value)}
                  opciones={campo.opciones.map((o) => ({ valor: o.valor, etiqueta: o.etiqueta }))}
                  ayuda={ayuda}
                />
              );
            }

            if (campo.tipo === 'enumlist') {
              const elegidos = new Set(
                Array.isArray(valor)
                  ? valor.map(String)
                  : String(valor ?? '')
                      .split(/\s*,\s*/)
                      .filter(Boolean),
              );

              return (
                <fieldset key={campo.clave} className={estilos.grupo}>
                  <legend className={estilos.leyendaGrupo}>
                    {campo.etiqueta}
                    {campo.requerido && ' *'}
                  </legend>

                  {campo.opciones.map((opcion) => (
                    <label key={opcion.valor} className={estilos.opcion}>
                      <input
                        type="checkbox"
                        checked={elegidos.has(opcion.valor)}
                        onChange={(e) => {
                          const siguiente = new Set(elegidos);
                          if (e.target.checked) siguiente.add(opcion.valor);
                          else siguiente.delete(opcion.valor);
                          cambiar(campo.clave, [...siguiente]);
                        }}
                      />
                      <span>{opcion.etiqueta}</span>
                    </label>
                  ))}

                  <span className={estilos.opcionAyuda}>{ayuda}</span>
                </fieldset>
              );
            }

            const esNumero = campo.tipo === 'int' || campo.tipo === 'number';

            return (
              <Input
                key={campo.clave}
                etiqueta={campo.unidad ? `${campo.etiqueta} (${campo.unidad})` : campo.etiqueta}
                requerido={campo.requerido}
                type={esNumero ? 'number' : 'text'}
                min={campo.min ?? undefined}
                max={campo.max ?? undefined}
                step={esNumero ? (campo.paso ?? (campo.tipo === 'int' ? 1 : 'any')) : undefined}
                value={valor === null || valor === undefined ? '' : String(valor)}
                onChange={(e) => cambiar(campo.clave, e.target.value)}
                ayuda={ayuda}
              />
            );
          })
        )}
      </div>
    </Modal>
  );
}
